import React, { useCallback, useEffect, useState } from 'react';
import user_icon from '../../../assets/icons/user.svg';
import monthly_submissions from '../../../assets/icons/driverDetail/monthlysubmissions.svg';
import exceed_alcohol_limit from '../../../assets/icons/driverDetail/exceedalcohollimit.svg';
import monthly_working from '../../../assets/icons/driverDetail/monthlyworking.svg';
import axiosInstance from 'config/axiosInstance';
import FetchFailed from 'reusable/FetchFailed';
import { useLatestRequest } from 'reusable/useLatestRequest';
import { fetchAll } from 'config/fetchAll';
import { EXCEED_FILTER_PARAM } from 'config/measurement';
import { durationText } from 'reusable/durationText';
import { businessDayStart, businessMonthRange, businessRangeQuery, toBusinessDate } from 'config/time';
import { useTranslation } from 'react-i18next';

interface DriverAlcoholSubmissionsTableProps {
  id?: string;
}
interface Profile {
  name?: string;
  phone?: string;
  email?: string;
  profile_picture?: string;
}
interface MonthlySubmissionTotal {
  count?: number;
}
interface MonthlySubmission {
  count?: number;
}
interface ExccedAlcoholLimitTotal {
  count?: number;
}
interface ExccedAlcoholLimit {
  count?: number;
}
interface MonthlyWorkingHoursTotal {
  totalHours: string;
}
interface MonthlyWorkingHours {
  monthlyHours: string;
}

const DriverViewStatus: React.FC<DriverAlcoholSubmissionsTableProps> = ({
  id,
}) => {
  const { t } = useTranslation('driver');
  const beginRequest = useLatestRequest();
  const month = toBusinessDate(new Date()).slice(0, 7);
  const requestKey = `${id}-${month}`;
  const [resultKey, setResultKey] = useState('');
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<unknown>(null);
  const [profile, setProfile] = useState<Profile | undefined>(undefined);
  const [monthlySubmissionsTotal, setMonthlySubmissionsTotal] = useState<
    MonthlySubmissionTotal | undefined
  >(undefined);
  const [monthlySubmissions, setMonthlySubmissions] = useState<
    MonthlySubmission | undefined
  >(undefined);
  const [exceedAlcoholLimitTotal, setExceedAlcoholLimitTotal] = useState<
    ExccedAlcoholLimitTotal | undefined
  >(undefined);
  const [exceedAlcoholLimit, setExceedAlcoholLimit] = useState<
    ExccedAlcoholLimit | undefined
  >(undefined);
  const [monthlyWorkingHoursTotal, setMonthlyWorkingHoursTotal] = useState<
    MonthlyWorkingHoursTotal | undefined
  >(undefined);
  const [monthlyWorkingHours, setMonthlyWorkingHours] = useState<
    MonthlyWorkingHours | undefined
  >(undefined);

  const calculateTotalHours = (attendances: any[]): number => {
    return attendances.reduce((total: number, attendance: any) => {
      const checkIn = new Date(attendance.check_in).getTime();
      const checkOut = new Date(attendance.check_out).getTime();
      if (checkOut > checkIn) {
        const hours = (checkOut - checkIn) / (1000 * 60 * 60);
        return total + hours;
      }

      return total;
    }, 0);
  };

  const fetchProfileData = useCallback(async () => {
    const isCurrent = beginRequest();
    setLoading(true);
    setFetchError(null);
    try {
      const { start, end } = businessMonthRange(new Date(businessDayStart(`${month}-01`)));
      const monthRange = businessRangeQuery('testing_time', start, end);
      // すべて同じ運転者・同じ月の結果が揃ってから反映する。
      const [profileResult, total, monthly, exceedTotal, exceedMonthly, totalAttendance, monthlyAttendance] = await Promise.all([
        axiosInstance.get(`/v1/drivers/${id}/`),
        axiosInstance.get(`v1/alcohol_tests/?user=${id}`),
        axiosInstance.get(`v1/alcohol_tests/?${monthRange}user=${id}`),
        axiosInstance.get(`v1/alcohol_tests/?${EXCEED_FILTER_PARAM}&user=${id}`),
        axiosInstance.get(`v1/alcohol_tests/?${EXCEED_FILTER_PARAM}&${monthRange}user=${id}`),
        fetchAll(`v1/attendances/?user=${id}`),
        fetchAll(`v1/attendances/?${businessRangeQuery('check_in', start, end)}user=${id}`),
      ]);
      if (!isCurrent()) return;
      setProfile(profileResult.data);
      setMonthlySubmissionsTotal(total.data);
      setMonthlySubmissions(monthly.data);
      setExceedAlcoholLimitTotal(exceedTotal.data);
      setExceedAlcoholLimit(exceedMonthly.data);
      setMonthlyWorkingHoursTotal({ totalHours: calculateTotalHours(totalAttendance).toFixed(2) });
      setMonthlyWorkingHours({ monthlyHours: calculateTotalHours(monthlyAttendance).toFixed(2) });
    } catch (error) {
      if (!isCurrent()) return;
      setFetchError(error);
    } finally {
      if (isCurrent()) {
        setResultKey(requestKey);
        setLoading(false);
      }
    }
  }, [id, month, requestKey, beginRequest]);

  useEffect(() => {
    fetchProfileData();
  }, [fetchProfileData]);

  if (loading || resultKey !== requestKey) return <p role="status">{t('ui.loading', { ns: 'common' })}</p>;
  if (fetchError) return <FetchFailed error={fetchError} onRetry={fetchProfileData} message={t('ui.detail-fetch-failed', { ns: 'common' })} />;

  return (
    <section className="flex flex-row w-full space-x-4">
      {/* User info or Profile */}
      <div className="bg-white rounded-md flex flex-row p-4 items-center space-x-4 w-1/4 truncate">
        <img
          src={profile?.profile_picture || user_icon}
          alt=""
          className="w-10 h-10 object-cover rounded-full"
        />
        <div className="flex flex-col text-left truncate">
          <h6 className="text-body-1 text-primary/400">
            {profile?.name || 'Unknown'}
          </h6>
          <p className="text-caption text-gray-400">
            +81 {profile?.phone || 'Unknown'}
          </p>
          <p className="text-caption text-gray-400">
            {profile?.email || 'Unknown'}
          </p>
        </div>
      </div>

      {/* Status data */}
      <div className="flex bg-white rounded-md flex-row p-4 space-x-4 w-3/4">
        {/* Monthly Submissions */}
        <div className="flex flex-1 flex-col">
          <div className="flex flex-row justify-between">
            <p className=" text-primary/300 text-title font-semi-bold">
              {monthlySubmissions?.count}
            </p>
            <img
              src={monthly_submissions}
              alt=""
              className="bg-primary/50 rounded-full p-2"
            />
          </div>
          <div>
            <h6 className="text-body-1 text-primary/400">
              {t('view.status.monthly-submission')}
            </h6>
            <p className="text-body-2 text-gray-400">
              {t('view.status.monthly-info')}
              {monthlySubmissionsTotal?.count} {t('view.status.monthly-info2')}
            </p>
          </div>
        </div>

        {/* Exceed Alcohol Limits */}
        <div className="flex flex-1 flex-col border-x px-4">
          <div className="flex flex-row justify-between">
            <p className=" text-primary/300 text-title font-semi-bold">
              {exceedAlcoholLimit?.count}
            </p>
            <img
              src={exceed_alcohol_limit}
              alt=""
              className="bg-primary/50 rounded-full p-2"
            />
          </div>
          <div>
            <h6 className="text-body-1 text-primary/400">
              {t('view.status.exceed-alcohol')}
            </h6>
            <p className="text-body-2 text-gray-400">
              {t('view.status.exceed-info')}
              {exceedAlcoholLimitTotal?.count} {t('view.status.exceed-info2')}
            </p>
          </div>
        </div>

        {/* Monthly Working Hours */}
        <div className="flex flex-1 flex-col">
          <div className="flex flex-row justify-between">
            <p className=" text-primary/300 text-title font-semi-bold">
              {Math.floor(Number(monthlyWorkingHours?.monthlyHours ?? 0))}{' '}
              <sub>/ 160</sub>
            </p>
            <img
              src={monthly_working}
              alt=""
              className="bg-primary/50 rounded-full p-2"
            />
          </div>
          <div>
            <h6 className="text-body-1 text-primary/400">
              {t('view.status.monthly-working')}
            </h6>
            <p className="text-body-2 text-gray-400">
              {/* EV-337（2026-10-14 PM 判断: 英語は 1 だけ単数）。以前は「1 hrs total」。 */}
              {t('view.status.working-total', {
                duration: durationText(
                  t,
                  'ui.duration-h',
                  { hours: Math.floor(Number(monthlyWorkingHoursTotal?.totalHours ?? 0)) },
                  { ns: 'common' }
                ),
              })}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DriverViewStatus;